import { useMemo } from 'react';
import { SubscriptionTier } from '@fabric/woody-client';
import { useQuerySubscription } from '../queries/useQuerySubscription.js';

const useGetPlanEntitlement = () => {
  const { data: subscription, error } = useQuerySubscription();
  return useMemo(() => {
    const currentTier = subscription?.tier || SubscriptionTier.FREE;
    if (!subscription || !!error) {
      return {
        currentTier,
        getPlanEntitlement: null
      };
    }
    const getPlanEntitlement = (entitlement) => {
      switch (entitlement) {
        case "voiceNote": {
          return {
            currentTier,
            isAllowed: () => !!subscription.entitlements?.aiVoiceAndMeetingRecordingEnabled
          };
        }
        case "integrationsEnabled": {
          return {
            currentTier,
            isAllowed: () => !!subscription.entitlements?.integrationsEnabled
          };
        }
        case "bulkImportEnabled": {
          return {
            currentTier,
            isAllowed: () => !!subscription.entitlements?.bulkImportEnabled
          };
        }
        case "maxFileSizeMB": {
          return {
            /**
            * returns whether the amount of bytes as a single file can be uploaded
            * @param fileSize bytes
            * @returns
            */
            currentTier,
            isAllowed: (fileSizeToUpload) => {
              const maxFileSizeMB = subscription.entitlements?.maxFileSizeMB ?? 0;
              const maxFileSizeBytes = maxFileSizeMB * 1e3 * 1e3;
              return maxFileSizeBytes >= fileSizeToUpload;
            }
          };
        }
        case "maxTotalStorageSize": {
          return {
            /**
            * returns whether the amount can be stored
            * @param storageSizeIncrease bytes
            * @returns
            */
            currentTier,
            isAllowed: (storageSizeIncrease) => {
              if (!subscription.usage.storage) {
                return false;
              }
              return (subscription.usage.storage.limit - subscription.usage.storage.used) * 1e3 * 1e3 > storageSizeIncrease;
            }
          };
        }
        case "maxResourceCount": {
          return {
            /**
            * @deprecated
            */
            currentTier,
            isAllowed: () => {
              console.warn("maxResourceCount entitlement is deprecated");
              return true;
            }
          };
        }
        case "maxSpaceCount": {
          return {
            /**
            * @deprecated
            */
            currentTier,
            isAllowed: () => {
              console.warn("maxResourceCount entitlement is deprecated");
              return true;
            }
          };
        }
        case "credits": {
          return {
            currentTier,
            isAllowed: (arg) => {
              if (!subscription.usage.credits) {
                return false;
              }
              const remainingCredits = subscription.usage.credits.limit - subscription.usage.credits.used;
              const creditsAboutToSpend = typeof arg === "number" ? arg : arg(subscription.creditOperationCosts);
              const allowed = remainingCredits > creditsAboutToSpend;
              return allowed;
            }
          };
        }
        case "recapsEnabled": {
          return {
            currentTier,
            isAllowed: () => !!subscription.entitlements?.recapsEnabled
          };
        }
        case "modelSwitchingEnabled": {
          return {
            currentTier,
            isAllowed: () => !!subscription.entitlements?.modelSwitchingEnabled
          };
        }
        case "audioTranscriptionEnabled": {
          return {
            currentTier,
            isAllowed: () => !!subscription.entitlements?.audioTranscriptionEnabled
          };
        }
        default: {
          return {
            currentTier,
            isAllowed: () => {
              console.warn(`${entitlement} entitlement is not implemented`);
              return true;
            }
          };
        }
      }
    };
    return {
      currentTier,
      getPlanEntitlement
    };
  }, [
    subscription,
    error
  ]);
};

export { useGetPlanEntitlement };
//# sourceMappingURL=usePlanEntitlement.js.map
